import { GetChannelsDayShowtimesDocument, type TvChannelDay } from '@lib/graphql/generated';
import { usePaginatedQuery } from '@src/lib/graphql/urql';
import { useTvGuideStore } from '@src/stores/tvguide';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { CHANNELCOUNT } from 'tvm-config';
import { ColumnView } from './ColumnView';
import { TimelineView } from './TimelineView';

export type TvGuideHandle = {
  changeScrollPosition: (count: number) => void;
};

export interface TvGuideProps {
  columnView: boolean;
  activeDay: Date;
  initialChannelEntries?: TvChannelDay[];
}

// A page that failed to load (e.g. API 429) has no data. Without the filter it
// becomes an undefined entry and crashes every component reading entry.channel.
function toChannelEntries(pages): TvChannelDay[] | undefined {
  return pages
    ?.flatMap((page) => page?.channelEntries?.data ?? [])
    .filter((entry): entry is TvChannelDay => Boolean(entry?.channel)) as TvChannelDay[] | undefined;
}

export const TvGuide = ({ columnView, activeDay, initialChannelEntries }) => {
  const updateTvCurrentTime = useTvGuideStore((state) => state.updateTvCurrentTime);

  const [{ data: channelsData, fetching, hasMorePages, fetchNextPage, currentPage }] = usePaginatedQuery({
    query: GetChannelsDayShowtimesDocument,
    variables: {
      page: 1,
      perPage: CHANNELCOUNT,
      date: format(activeDay, 'yyyy-MM-dd'),
    },
    // requestPolicy: 'cache-first',
  });

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const channelEntriesTemp = useRef<TvChannelDay[]>(initialChannelEntries);

  useEffect(() => {
    if (!fetching) channelEntriesTemp.current = toChannelEntries(channelsData) ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetching, channelsData]);

  // Use initialChannelEntries during SSR + hydration for consistency,
  // then switch to urql data after mount
  const channelEntries = hasMounted
    ? ((fetching ? channelEntriesTemp.current : toChannelEntries(channelsData)) ?? toChannelEntries(channelsData))
    : initialChannelEntries;

  // Guard all query-derived values during SSR + hydration to prevent mismatches
  const showFetching = hasMounted && fetching;
  const showHasMorePages = hasMounted && hasMorePages;
  const showCurrentPage = hasMounted ? currentPage : null;

  useEffect(() => {
    const interval = setInterval(() => updateTvCurrentTime(), 5000);
    return () => {
      clearInterval(interval);
    };
  }, [updateTvCurrentTime]);

  return (
    <div className="relative w-full">
      {columnView ? (
        <ColumnView
          channelEntries={channelEntries}
          activeDay={activeDay}
          fetching={showFetching}
          onFetchMore={fetchNextPage}
          hasMorePages={showHasMorePages}
          currentPage={showCurrentPage}
        />
      ) : (
        <TimelineView
          channelEntries={channelEntries}
          activeDay={activeDay}
          fetching={showFetching}
          onFetchMore={fetchNextPage}
          hasMorePages={showHasMorePages}
          currentPage={showCurrentPage}
        />
      )}
    </div>
  );
};
